import type { AutodeskSavedForm, AutodeskSavedValue } from "../../../../backend/src/shared/autodeskSavedForm";
import { Fragment } from "react";
import type { ReactNode } from "react";
import type { AutodeskDailyLogMappingPlan, AutodeskDailyLogDisplayGroup } from "../../../../backend/src/shared/autodeskFormTemplate";
import { autodeskDisplayGroupForItem } from "../../../../backend/src/shared/autodeskFormTemplate";
import type { ConsoleRow } from "../api";
import { C } from "../tokens";

/** Lark A08: keep the saved section/field hierarchy, including empty and
 * manual controls. No current project template is accepted by this view. */
export function autodeskReportSections(plan: AutodeskDailyLogMappingPlan, rows: ConsoleRow[]) {
  const assigned = new Map<string, ConsoleRow[]>();
  const unmatched: ConsoleRow[] = [];
  const groups = plan.displayGroups ?? [];
  for (const row of rows) {
    // A stale target cannot be silently reassigned to a similarly named field
    // in another layout or to the canonical Notes fallback.
    const target = row.autodeskFieldTarget;
    const group = target
      ? target.layoutId === plan.layoutId
        ? groups.find(group => group.fieldId === target.fieldId && group.sectionId === target.sectionId)
        : undefined
      : autodeskDisplayGroupForItem(plan, { category: row.category ?? "" });
    if (!group || !plan.sections.some(section => section.id === group.sectionId)) { unmatched.push(row); continue; }
    assigned.set(group.id, [...(assigned.get(group.id) ?? []), row]);
  }
  const sections = [...plan.sections].sort((a, b) => a.displayIndex - b.displayIndex).map(section => ({
    ...section,
    groups: groups.filter(group => group.sectionId === section.id).map(group => {
      const binding = plan.questionBindings?.find(binding => binding.fieldId === group.fieldId && binding.sectionId === section.id);
      return { ...group, label: binding?.fieldLabel ?? group.label,
        presets: binding?.valueKind === "toggle" || group.valueKind === "toggle" ? ["Yes", "No", "N/A"] : binding?.presets ?? group.presets, rows: assigned.get(group.id) ?? [] };
    }),
  }));
  return { sections, unmatched };
}

/** Preserve explicit zero/false and multiple selections. Missing is distinct. */
export function autodeskDisplayValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "Not recorded";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.length ? value.map(autodeskDisplayValue).join(", ") : "No selections";
  return String(value);
}

export function savedAutodeskDisplayValue(value?: AutodeskSavedValue): string {
  if (!value) return "Not recorded";
  if (value.unavailable) return "Saved value unavailable · View in Autodesk";
  if (value.signatureRecorded) return value.name ? `Signed by ${value.name} · View signature in Autodesk` : "Signature recorded · View in Autodesk";
  if (value.toggleVal !== undefined) {
    const normalized = String(value.toggleVal).toLowerCase();
    return normalized === "false" ? "No" : normalized === "true" ? "Yes" : ["na", "n/a", "not_applicable"].includes(normalized) ? "N/A" : String(value.toggleVal);
  }
  if (value.uidVal) return value.textVal || "Name unavailable";
  if (value.timespanVal !== undefined) return `${value.timespanVal / 3_600_000} h`;
  return autodeskDisplayValue(value.textVal ?? value.choiceVal ?? value.arrayVal ?? value.numberVal ?? value.integerVal ?? value.dateVal ?? value.timeVal);
}

export function AutodeskReportSections({ plan, rows, renderRow, onSelect, saved, published = false, loading = false }: {
  plan: AutodeskDailyLogMappingPlan;
  saved?: AutodeskSavedForm;
  published?: boolean;
  loading?: boolean;
  rows: ConsoleRow[];
  renderRow: (row: ConsoleRow, index: number) => ReactNode;
  onSelect: (id: string) => void;
}) {
  const presentation = autodeskReportSections(plan, rows);
  // Never mix values from a different frozen Form layout into this hierarchy.
  const report = saved?.layoutId === plan.layoutId ? saved : undefined;
  const unavailable = loading ? "Loading saved value…" : "Saved value unavailable";
  const savedQuestion = (group: AutodeskDailyLogDisplayGroup) => {
    const field = group.fieldId ?? plan.sections.find(section => section.id === group.sectionId)?.fields.find(field => field.type === "question")?.uid;
    const answer = report?.questions.find(value => value.fieldId === field);
    return <div data-autodesk-answer={field}><p style={{ margin: "5px 0", fontSize: 14 }}>{report ? savedAutodeskDisplayValue(answer) : unavailable}</p>
      {answer?.notes && <p style={hintStyle}>{answer.notes}</p>}</div>;
  };
  const weather = <section data-autodesk-section="weather" style={{ padding: "16px 20px", borderTop: `1px solid ${C.hair}` }}>
    <h3 style={{ fontSize: 16, margin: "0 0 10px" }}>Weather</h3>
    {!report ? <p style={hintStyle}>{unavailable}</p> : !report.weather ? <p style={hintStyle}>Not recorded</p> : <>
      <p>{report.weather.summary}{report.weather.precipitationMm !== undefined ? ` · ${(report.weather.precipitationMm / 25.4).toFixed(2)} in precipitation` : ""}</p>
      <div style={{ overflowX: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}><thead><tr>{["Time", "Conditions", "Temperature", "Wind", "Humidity"].map(label => <th key={label} style={cellStyle}>{label}</th>)}</tr></thead>
        <tbody>{report.weather.hours.map(hour => <tr key={hour.hour}>
          <td style={cellStyle}>{hour.hour.slice(0, 5)}</td><td style={cellStyle}>{hour.summary}</td>
          <td style={cellStyle}>{hour.temperatureC === undefined ? "Not recorded" : `${Math.round(hour.temperatureC * 9 / 5 + 32)}°F`}</td>
          <td style={cellStyle}>{hour.windKph === undefined ? "Not recorded" : `${Math.round(hour.windKph / 1.609344)} mph`}</td>
          <td style={cellStyle}>{hour.humidity === undefined ? "Not recorded" : `${Math.round(hour.humidity * 100)}%`}</td>
        </tr>)}</tbody></table></div>
    </>}
  </section>;
  const table = (group: AutodeskDailyLogDisplayGroup, entries: ConsoleRow[]) => {
    const section = plan.sections.find(section => section.id === group.sectionId)!;
    const bindings = plan.tableBindings?.filter(binding => binding.sectionId === group.sectionId && binding.schema === group.schema) ?? [];
    if (published || report) {
      const stored = report?.tables.find(table => table.schema === group.schema);
      return <div style={{ overflowX: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead><tr>{section.columns.map(column => <th key={column.uid} style={cellStyle}>{column.label}</th>)}</tr></thead>
        <tbody>{!stored?.rows.length && <tr><td colSpan={Math.max(section.columns.length, 1)} style={cellStyle}>{!report || !stored ? unavailable : "No entries recorded"}</td></tr>}
          {stored?.rows.map(row => <tr key={row.id}>{section.columns.map(column => <td key={column.uid} style={cellStyle}>{savedAutodeskDisplayValue(row.columns.find(cell => cell.columnId === column.uid))}</td>)}</tr>)}
        </tbody></table>{columnOptions(section.columns)}</div>;
    }
    return <div style={{ overflowX: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <thead><tr><th style={cellStyle}>Entry</th>{section.columns.map(column => <th key={column.uid} style={cellStyle}>{column.label}</th>)}</tr></thead>
      <tbody>{!entries.length && <tr><td colSpan={section.columns.length + 1} style={cellStyle}>No LedAI entries in this section.</td></tr>}{entries.map(row => <tr key={row.id}>
        <td style={cellStyle}><button type="button" onClick={() => onSelect(row.id)} style={{ color: C.ink, textAlign: "left", textDecoration: "underline" }}>{row.title}</button></td>
        {section.columns.map(column => {
          const binding = bindings.find(binding => binding.sourceCategory === row.category)?.columns.find(binding => binding.columnId === column.uid);
          const value = binding?.mode === "title" ? row.title : binding?.sourceFieldKeys.map(key => row.fieldValues?.[key]).find(value => value !== null && value !== undefined && value !== "");
          if (binding?.columnType === "uid_val") {
            const reference = binding.sourceFieldKeys.map(key => row.autodeskReferences?.[key]).find(Boolean);
            const label = reference?.label ?? (reference || value ? "Name unavailable" : "Not recorded");
            return <td key={column.uid} style={cellStyle}>{label}</td>;
          }
          return <td key={column.uid} style={cellStyle}>{autodeskDisplayValue(value)}</td>;
        })}
      </tr>)}</tbody>
    </table>{columnOptions(section.columns)}</div>;
  };
  return <div data-testid="autodesk-report-sections" data-layout-id={plan.layoutId}>
    {!presentation.sections.some(section => section.kind === "basic_information") && published && weather}
    {presentation.sections.map(section => <Fragment key={section.id}><section data-autodesk-section={section.id} style={{ padding: "16px 20px", borderTop: `1px solid ${C.hair}` }}>
      <h3 style={{ fontSize: 16, margin: "0 0 10px", fontWeight: 650 }}>{section.label}</h3>
      {section.description && <p style={{ fontSize: 13, color: C.sub }}>{section.description}</p>}
      {section.groups.map(group => <div key={group.id} data-autodesk-field={group.fieldId ?? group.id} style={{ marginTop: 10 }}>
        {group.label !== section.label && <h4 style={{ margin: "0 0 5px", fontSize: 14, fontWeight: 600 }}>{group.label}{group.required ? " *" : ""}</h4>}
        {group.kind === "question" && !!group.presets?.length && <div aria-label={`Response options for ${group.label}`} style={{ margin: "6px 0 10px" }}>
          <span style={{ fontSize: 12, color: C.sub }}>Response options</span>
          <ul style={{ display: "flex", flexWrap: "wrap", gap: 6, listStyle: "none", padding: 0, margin: "5px 0" }}>
            {group.presets.map((preset, index) => <li key={index} style={{ border: `1px solid ${C.hair}`, borderRadius: 6, padding: "4px 8px", fontSize: 13 }}>{preset}</li>)}
          </ul>
        </div>}
        {group.kind === "basic_information" ? report ? <dl style={{ fontSize: 14 }}>
          <dt>Name</dt><dd>{report.name || "Not recorded"}</dd><dt>Date</dt><dd>{report.formDate || "Not recorded"}</dd>
          <dt>Status</dt><dd>{report.status === "inProgress" ? "In progress" : report.status}</dd><dt>Description</dt><dd>{report.description || "Not recorded"}</dd>
          <dt>Location</dt><dd>{report.locationId ? "View location in Autodesk" : "Not recorded"}</dd></dl>
          : <p style={hintStyle}>{published ? unavailable : "Form date, name, location and description are managed in Autodesk."}</p>
          : (published || report) && (group.kind === "question" || group.kind === "manual") ? savedQuestion(group)
          : (published || report) && section.kind === "notes" ? <p style={hintStyle}>{report ? report.notes || "Not recorded" : unavailable}</p>
          : group.kind === "manual" ? <p style={hintStyle}>{group.required ? "Required · " : ""}Complete or view this field in Autodesk.</p>
          : group.kind === "table" ? table(group, group.rows)
          : group.rows.length ? group.rows.map(renderRow)
            : <p style={hintStyle}>No LedAI entries in this section.</p>}
        {(published || report) && group.rows.length > 0 && <details style={{ marginTop: 12 }}>
          <summary style={{ cursor: "pointer", fontSize: 13 }}>Entries from this walk ({group.rows.length})</summary>
          {group.rows.map(renderRow)}
        </details>}
      </div>)}
      {!section.groups.length && <p style={hintStyle}>No LedAI entries in this section.</p>}
    </section>{section.kind === "basic_information" && (published || report) && weather}</Fragment>)}
    {presentation.unmatched.length > 0 && <section data-testid="autodesk-unmapped-entries" style={{ padding: "16px 20px", borderTop: `1px solid ${C.hair}` }}>
      <h3 style={{ fontSize: 16 }}>Additional entries</h3>
      <p style={hintStyle}>These entries have no matching field in this report’s saved template.</p>
      {presentation.unmatched.map(renderRow)}
    </section>}
  </div>;
}
const cellStyle = { padding: "10px 12px", borderBottom: `1px solid ${C.hair}`, textAlign: "left" as const, verticalAlign: "top" as const };
const hintStyle = { fontSize: 13, color: C.sub, margin: "5px 0" };

function columnOptions(columns: AutodeskDailyLogMappingPlan["sections"][number]["columns"]) {
  return columns.some(column => column.presets.length > 0) && <details style={{ margin: "10px 0", fontSize: 13 }}>
      <summary style={{ cursor: "pointer", color: C.sub }}>Column options</summary>
      <dl style={{ margin: "8px 0" }}>{columns.filter(column => column.presets.length > 0).map(column => <div key={column.uid} style={{ marginTop: 6 }}>
        <dt style={{ fontWeight: 600 }}>{column.label}</dt>
        <dd style={{ margin: "3px 0 0", color: C.sub }}>{column.presets.join(" · ")}</dd>
      </div>)}</dl>
    </details>;
}
